"use client";
import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import styles from "./ServerSupportSLASection.module.css";
import { serverSupportSlaMetrics } from "@/app/services/data/server-support";

const ServerSupportSLASection = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const sectionEl = sectionRef.current;
    if (!sectionEl) return;

    const cards = Array.from(sectionEl.querySelectorAll(`.${styles.card}`));
    const radius = 300;
    const proximity = radius * 0.5;
    const fadeDistance = radius * 0.75;

    const updateCardGlow = (card, mouseX, mouseY) => {
      const rect = card.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const distance =
        Math.hypot(mouseX - cx, mouseY - cy) -
        Math.max(rect.width, rect.height) / 2;
      const effective = Math.max(0, distance);

      let intensity = 0;
      if (effective <= proximity) intensity = 1;
      else if (effective <= fadeDistance)
        intensity = (fadeDistance - effective) / (fadeDistance - proximity);

      const relX = ((mouseX - rect.left) / rect.width) * 100;
      const relY = ((mouseY - rect.top) / rect.height) * 100;

      card.style.setProperty("--glow-x", `${relX}%`);
      card.style.setProperty("--glow-y", `${relY}%`);
      card.style.setProperty("--glow-intensity", intensity.toString());
      card.style.setProperty("--glow-radius", `${radius}px`);
    };

    const onMouseMove = (e) => {
      const rect = sectionEl.getBoundingClientRect();
      const inside =
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom;
      if (!inside) {
        cards.forEach((c) => c.style.setProperty("--glow-intensity", "0"));
        return;
      }
      cards.forEach((c) => updateCardGlow(c, e.clientX, e.clientY));
    };

    document.addEventListener("mousemove", onMouseMove);

    // Per-card tilt, magnetism and click ripple
    const listeners = cards.map((card) => {
      const handleEnter = () => {
        gsap.to(card, { y: -2, duration: 0.2, ease: "power2.out" });
      };
      const handleLeave = () => {
        gsap.to(card, {
          rotateX: 0,
          rotateY: 0,
          x: 0,
          y: 0,
          duration: 0.25,
          ease: "power2.out",
        });
        card.style.setProperty("--glow-intensity", "0");
      };
      const handleMove = (e) => {
        const r = card.getBoundingClientRect();
        const x = e.clientX - r.left;
        const y = e.clientY - r.top;
        const cx = r.width / 2;
        const cy = r.height / 2;
        const rotateX = ((y - cy) / cy) * -8;
        const rotateY = ((x - cx) / cx) * 8;
        const magnetX = (x - cx) * 0.04;
        const magnetY = (y - cy) * 0.04;
        gsap.to(card, {
          rotateX,
          rotateY,
          x: magnetX,
          y: magnetY,
          duration: 0.12,
          ease: "power2.out",
          transformPerspective: 1000,
        });
      };
      const handleClick = (e) => {
        const r = card.getBoundingClientRect();
        const x = e.clientX - r.left;
        const y = e.clientY - r.top;
        const maxD = Math.max(
          Math.hypot(x, y),
          Math.hypot(x - r.width, y),
          Math.hypot(x, y - r.height),
          Math.hypot(x - r.width, y - r.height)
        );
        const ripple = document.createElement("div");
        const accent =
          getComputedStyle(card).getPropertyValue("--accent-rgb").trim() ||
          "245, 131, 39";
        ripple.style.cssText = `position:absolute;width:${maxD * 2}px;height:${
          maxD * 2
        }px;border-radius:50%;left:${x - maxD}px;top:${
          y - maxD
        }px;pointer-events:none;background:radial-gradient(circle, rgba(${accent},0.35) 0%, rgba(${accent},0.15) 30%, transparent 70%);`;
        card.appendChild(ripple);
        gsap.fromTo(
          ripple,
          { scale: 0, opacity: 1 },
          {
            scale: 1,
            opacity: 0,
            duration: 0.8,
            ease: "power2.out",
            onComplete: () => ripple.remove(),
          }
        );
      };
      card.addEventListener("mouseenter", handleEnter);
      card.addEventListener("mouseleave", handleLeave);
      card.addEventListener("mousemove", handleMove);
      card.addEventListener("click", handleClick);
      return () => {
        card.removeEventListener("mouseenter", handleEnter);
        card.removeEventListener("mouseleave", handleLeave);
        card.removeEventListener("mousemove", handleMove);
        card.removeEventListener("click", handleClick);
      };
    });

    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      listeners.forEach((off) => off());
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className={styles.section}
      aria-label="SLA and reliability metrics"
    >
      <div className={styles.inner}>
        <div className={styles.header}>
          <span className={styles.eyebrow}>Reliability & SLAs</span>
          <h3 className={styles.title}>We commit to measurable outcomes</h3>
          <p className={styles.desc}>
            Transparent, enforceable commitments backed by on‑call rotations,
            observability, and tested recovery—so your workloads stay healthy
            around the clock.
          </p>
        </div>
        <div className={styles.ring} aria-hidden="true" />
        <div className={styles.grid}>
          {serverSupportSlaMetrics.map((m, i) => {
            const sizeClass = i === 2 || i === 3 ? styles.lg : styles.sm;
            return (
              <div key={i} className={`${styles.card} ${sizeClass}`}>
                <div className={styles.glow} aria-hidden="true" />
                <div className={styles.row}>
                  {m.icon && (
                    <span className={styles.icon} aria-hidden="true">
                      {m.icon}
                    </span>
                  )}
                  <div className={styles.value}>{m.value}</div>
                  {m.delta && (
                    <span
                      className={`${styles.delta} ${m.good ? styles.good : ""}`}
                    >
                      {m.delta}
                    </span>
                  )}
                </div>
                <div className={styles.label}>{m.label}</div>
                {m.note && <div className={styles.note}>{m.note}</div>}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServerSupportSLASection;
